/**
 * ETAP 7 – mounts the page routes for one language.
 * The English shell forces i18n to "en" and rewrites internal links so that
 * navigation stays inside the /en/ tree.
 */
import { useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HOME_ROUTE, INTERNAL_ROUTES, PUBLIC_ROUTES } from "@/routes/appRoutes";
import NotFound from "@/pages/NotFound";
import { EN_PREFIX, isEnPath, withEnPrefix } from "@/lib/locale";

const LocaleShell = ({ lang }: { lang: "pl" | "en" }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isEn = lang === "en";

  useEffect(() => {
    if (isEn && i18n.language?.slice(0, 2) !== "en") i18n.changeLanguage("en");
  }, [isEn, i18n]);

  useEffect(() => {
    document.documentElement.lang = isEn ? "en" : "pl";
  }, [isEn]);

  // Keep internal navigation inside /en/ without touching every Link in the app.
  useEffect(() => {
    if (!isEn) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;
      if (anchor.target && anchor.target !== "_self") return;
      const url = new URL(href, window.location.origin);
      if (isEnPath(url.pathname) || /\.[a-z0-9]+$/i.test(url.pathname)) return;
      event.preventDefault();
      navigate(`${withEnPrefix(url.pathname)}${url.search}${url.hash}`);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isEn, navigate]);

  const routes = isEn ? PUBLIC_ROUTES : [...PUBLIC_ROUTES, ...INTERNAL_ROUTES];

  return (
    <Routes>
      <Route index element={HOME_ROUTE.element} />
      {routes.map((r) => (
        <Route key={`${EN_PREFIX}-${r.path}`} path={r.path} element={r.element} />
      ))}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default LocaleShell;
